import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLeads, Lead } from '../contexts/LeadsContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Search,
  Visibility,
  Phone,
  Warning,
} from '@mui/icons-material';

export default function FollowUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { leads } = useLeads();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Get current date for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Month/Year filter - default to current month/year
  const [selectedMonth, setSelectedMonth] = useState<number>(() => {
    const params = new URLSearchParams(location.search);
    const urlMonth = params.get('month');
    return urlMonth ? parseInt(urlMonth) : new Date().getMonth() + 1;
  });

  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const params = new URLSearchParams(location.search);
    const urlYear = params.get('year');
    return urlYear ? parseInt(urlYear) : new Date().getFullYear();
  });

  // Filter for overdue only
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  // Read URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlMonth = params.get('month');
    const urlYear = params.get('year');
    const urlStatus = params.get('status');

    if (urlMonth) setSelectedMonth(parseInt(urlMonth));
    if (urlYear) setSelectedYear(parseInt(urlYear));
    if (urlStatus === 'overdue') {
      setShowOverdueOnly(true);
      setSelectedMonth(0); // All months
      setSelectedYear(0);  // All years
    } else if (urlStatus) {
      setStatusFilter(urlStatus);
    }
  }, [location.search]);

  // Filter leads that have follow-up dates (exclude won and lost leads)
  const leadsWithFollowUp = leads.filter(lead => lead.next_follow_up && lead.status !== 'won' && lead.status !== 'lost');

  // Filter by selected month/year and other filters
  const filteredLeads = leadsWithFollowUp.filter(lead => {
    const followUpDate = new Date(lead.next_follow_up!);
    followUpDate.setHours(0, 0, 0, 0);

    // Overdue filter - if enabled, only show overdue items
    if (showOverdueOnly) {
      if (followUpDate >= today) return false;
    }

    // Month/Year filter (only apply if not showing overdue only)
    let matchesMonth = true;
    if (!showOverdueOnly) {
      if (selectedMonth !== 0) {
        if (selectedYear === 0) {
          matchesMonth = followUpDate.getMonth() === (selectedMonth - 1);
        } else {
          matchesMonth = followUpDate.getMonth() === (selectedMonth - 1) && followUpDate.getFullYear() === selectedYear;
        }
      } else if (selectedYear !== 0) {
        matchesMonth = followUpDate.getFullYear() === selectedYear;
      }
    }

    // Search filter
    const matchesSearch =
      (lead.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.phone || '').includes(searchTerm) ||
      (lead.postcode && lead.postcode.toLowerCase().includes(searchTerm.toLowerCase()));

    // Status filter
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;

    return matchesMonth && matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Sort by follow-up date (earliest first)
    return new Date(a.next_follow_up!).getTime() - new Date(b.next_follow_up!).getTime();
  });

  // Calculate days until follow-up
  const getDaysUntil = (followUpDate: string) => {
    const date = new Date(followUpDate);
    date.setHours(0, 0, 0, 0);
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      new: 'primary',
      no_reply: 'default',
      not_ready: 'warning',
      contacted: 'secondary',
      quoted: 'warning',
      won: 'success',
      lost: 'error',
    };
    return colors[status] || 'default';
  };

  // Format status label
  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get unique statuses from leads with follow-ups
  const availableStatuses = Array.from(new Set(leadsWithFollowUp.map(lead => lead.status))).sort();

  // Count stats
  const overdueCount = filteredLeads.filter(lead => getDaysUntil(lead.next_follow_up!) < 0).length;
  const todayCount = filteredLeads.filter(lead => getDaysUntil(lead.next_follow_up!) === 0).length;
  const upcomingCount = filteredLeads.filter(lead => getDaysUntil(lead.next_follow_up!) > 0).length;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Header with filters */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'flex-end',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
          <Button
            variant={showOverdueOnly ? 'contained' : 'outlined'}
            color="error"
            startIcon={<Warning />}
            onClick={() => {
              setShowOverdueOnly(!showOverdueOnly);
              if (!showOverdueOnly) {
                setSelectedMonth(0);
                setSelectedYear(0);
              }
            }}
            sx={{ minWidth: 160, height: 40 }}
          >
            {showOverdueOnly ? 'Showing Overdue' : 'Show Overdue Only'}
          </Button>
          <FormControl size="small" sx={{ minWidth: 120 }} disabled={showOverdueOnly}>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              <MenuItem value={0}>All Months</MenuItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }} disabled={showOverdueOnly}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              <MenuItem value={0}>All Years</MenuItem>
              {[2025, 2026, 2027].map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Stats Cards - matching Leads page style */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(4, 1fr)',
          md: 'repeat(4, 1fr)'
        },
        gap: { xs: 1, sm: 1.5, md: 2 },
        mb: 2.5
      }}>
        {/* Overdue */}
        <Card
          onClick={() => {
            setShowOverdueOnly(!showOverdueOnly);
            if (!showOverdueOnly) {
              setSelectedMonth(0);
              setSelectedYear(0);
            }
          }}
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: showOverdueOnly ? '2px solid #f44336' : 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Overdue {showOverdueOnly && '(Active)'}
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, color: '#f44336' }}>
              {overdueCount}
            </Typography>
          </CardContent>
        </Card>

        {/* Today */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Today
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, color: '#ff9800' }}>
              {todayCount}
            </Typography>
          </CardContent>
        </Card>

        {/* Upcoming */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Upcoming
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, color: '#2196f3' }}>
              {upcomingCount}
            </Typography>
          </CardContent>
        </Card>

        {/* Total */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Total
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {filteredLeads.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' }
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
              <TextField
                fullWidth
                placeholder="Search customer, phone, postcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' }, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {filteredLeads.length} follow-ups found
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Follow-ups Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 600, sm: 750, md: 900 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' }
              }}>
                Customer
              </TableCell>
              <TableCell sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' },
                display: { xs: 'none', md: 'table-cell' }
              }}>
                Postcode
              </TableCell>
              <TableCell sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' },
                display: { xs: 'none', sm: 'table-cell' }
              }}>
                Title
              </TableCell>
              <TableCell sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' }
              }}>
                Status
              </TableCell>
              <TableCell sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' }
              }}>
                Follow-up Date
              </TableCell>
              <TableCell sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' },
                display: { xs: 'none', md: 'table-cell' }
              }}>
                Days
              </TableCell>
              <TableCell align="right" sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' }
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLeads
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((lead) => {
                const daysUntil = getDaysUntil(lead.next_follow_up!);
                const isOverdue = daysUntil < 0;
                const isToday = daysUntil === 0;

                return (
                  <TableRow
                    key={lead.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        backgroundColor: 'action.hover',
                      }
                    }}
                    onClick={() => navigate(`/leads/${lead.id}/edit`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {lead.customer_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {lead.phone}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {lead.postcode || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" fontWeight="bold">
                        {lead.title || '-'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Created: {new Date(lead.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatus(lead.status)}
                        size="small"
                        color={getStatusColor(lead.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(lead.next_follow_up!).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                          color: isOverdue ? '#f44336' : isToday ? '#ff9800' : '#2196f3'
                        }}
                      >
                        {isOverdue
                          ? `${Math.abs(daysUntil)} days overdue`
                          : isToday
                            ? 'Today'
                            : `In ${daysUntil} days`
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Lead">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads/${lead.id}/edit`);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Call">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `tel:${lead.phone}`;
                          }}
                        >
                          <Phone />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            {filteredLeads.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    No follow-ups found for the selected filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredLeads.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}
